/**
 * Shared PDF / PNG Export Utility
 * --------------------------------------------------------------------------
 * Every export in the app renders the *same DOM element the user sees in the
 * preview* via html2canvas, then either embeds the resulting canvas into a
 * jsPDF document (PDF) or saves it directly (PNG). Because there is a single
 * capture path, the downloaded file can never drift away from the on-screen
 * preview — the only thing excluded from a capture is the surrounding UI
 * chrome (toolbars, buttons), which lives outside the captured node.
 *
 * Public helpers:
 *   captureElementToCanvas(element, options)  -> HTMLCanvasElement
 *   exportElementPDF(element, filename, opts) -> Promise<string>
 *   exportElementPNG(element, filename, opts) -> Promise<string>
 *
 * Domain wrappers (all route through the helpers above):
 *   exportReceiptPDF / exportReceiptPNG
 *   exportInvoicePDF / exportInvoicePNG
 *   exportPaymentPDF (legacy alias of exportReceiptPDF)
 *
 * jsPDF + html2canvas are dynamically imported so they stay out of the
 * initial bundle (~150KB+ saved).
 */

let jsPDFModule = null

async function loadJsPDF() {
  if (!jsPDFModule) {
    const module = await import('jspdf')
    jsPDFModule = module.jsPDF
  }
  return jsPDFModule
}

let html2canvasModule = null

async function loadHtml2Canvas() {
  if (!html2canvasModule) {
    // html2canvas-pro is a maintained fork with proper flexbox gap,
    // clip-path, and modern CSS support — fixes layout collapse on capture.
    const mod = await import('html2canvas-pro')
    html2canvasModule = mod.default || mod
  }
  return html2canvasModule
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Core shared capture / export helpers                                      */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Capture a live DOM element to a canvas using html2canvas.
 * This is the single source of truth for every PDF/PNG export in the app —
 * whatever the user sees in the preview is exactly what gets captured.
 *
 * @param {HTMLElement} element - The DOM node to capture (e.g. .receipt-document)
 * @param {Object} [options]
 * @param {number} [options.scale=3] - Render scale for crisp output
 * @param {string} [options.backgroundColor='#ffffff']
 * @returns {Promise<HTMLCanvasElement>}
 */

/**
 * Wait for all web fonts (@font-face) to finish loading before capturing.
 *
 * html2canvas rasterizes the DOM synchronously. If custom fonts (e.g. Outfit,
 * system font stacks with web-font fallbacks) haven't loaded yet, it falls
 * back to substitute metrics — different line-heights, letter-spacing, and
 * weight — which causes layout collapse (e.g. the receipt subtitle jamming
 * into the orange banner) and bolder/tighter text in the export vs. preview.
 *
 * We explicitly load the specific font families/weights used in the captured
 * documents, then await document.fonts.ready as a final gate. This guarantees
 * the canvas is rasterized with the exact same font metrics the browser is
 * showing on screen.
 */
async function waitForFontsReady() {
  if (typeof document === 'undefined' || !document.fonts) return

  // Explicitly request the specific font faces used in receipts/invoices/
  // certificates so the browser starts loading them (if it hasn't already).
  // document.fonts.load() resolves once the face is loaded; we don't need to
  // await each individually — requesting them kicks off loading and the
  // .ready promise below gates on all pending loads finishing.
  const fontSpecs = [
    // Outfit (display/headings) — used in receipt & invoice headers
    '800 14px "Outfit"',
    '800 15px "Outfit"',
    '700 11px "Outfit"',
    '500 10px "Outfit"',
    '700 17px "Outfit"',
    '800 19px "Outfit"',
    // Plus Jakarta Sans (UI/body) — used in surrounding elements
    '400 12px "Plus Jakarta Sans"',
    '500 12px "Plus Jakarta Sans"',
    '600 12px "Plus Jakarta Sans"',
    '700 12px "Plus Jakarta Sans"',
  ]
  try {
    await Promise.all(
      fontSpecs.map((spec) => document.fonts.load(spec).catch(() => {})),
    )
  } catch {
    // Ignore individual load failures — .ready is the real gate.
  }

  // Gate on the FontFaceSet settling — all @font-face loads complete.
  try {
    await document.fonts.ready
  } catch {
    // If the font API throws, proceed anyway (better to capture than hang).
  }
}

export async function captureElementToCanvas(element, options = {}) {
  if (!element) throw new Error('No element provided for capture')

  // Wait for web fonts to be ready so html2canvas rasterizes with the same
  // font metrics the browser is showing on screen — prevents the layout
  // collapse / weight drift seen when capturing before fonts load.
  await waitForFontsReady()

  const html2canvas = await loadHtml2Canvas()
  return html2canvas(element, {
    scale: options.scale || 3,
    useCORS: true,
    allowTaint: true,
    backgroundColor: options.backgroundColor || '#ffffff',
    logging: false,
    ...options,
  })
}

/**
 * Save a canvas as a downloadable PNG file.
 * @param {HTMLCanvasElement} canvas
 * @param {string} filename
 */
export function saveCanvasAsPNG(canvas, filename) {
  const link = document.createElement('a')
  link.download = filename
  link.href = canvas.toDataURL('image/png', 1.0)
  document.body.appendChild(link)
  link.click()
  link.remove()
}

/**
 * Export a DOM element to a PDF file.
 * The captured canvas is fit to a single page (width-first, then height) and
 * centered horizontally.
 *
 * @param {HTMLElement} element
 * @param {string} filename
 * @param {Object} [options]
 * @param {number} [options.scale=3]
 * @param {'portrait'|'landscape'} [options.orientation='landscape']
 * @param {string|[number,number]} [options.format='a4']
 * @param {string} [options.backgroundColor='#ffffff']
 * @returns {Promise<string>} filename
 */
export async function exportElementPDF(element, filename, options = {}) {
  const jsPDF = await loadJsPDF()
  const canvas = await captureElementToCanvas(element, {
    scale: options.scale || 3,
    backgroundColor: options.backgroundColor,
  })

  const orientation = options.orientation || 'landscape'
  const format = options.format || 'a4'
  const doc = new jsPDF({ orientation, unit: 'mm', format })

  const pw = doc.internal.pageSize.getWidth()
  const ph = doc.internal.pageSize.getHeight()

  // Fit the canvas uniformly into the page, preserving the canvas's actual
  // pixel aspect ratio. Width-first: scale to page width, then derive height
  // from the canvas's own ratio. If that overflows the page height, fall back
  // to height-first scaling. Both dimensions are always derived from ONE
  // target dimension and canvas.width/canvas.height — never set independently
  // (which would stretch/squeeze the image on one axis).
  let pdfW = pw
  let pdfH = (canvas.height * pdfW) / canvas.width
  if (pdfH > ph) {
    pdfH = ph
    pdfW = (canvas.width * pdfH) / canvas.height
  }
  const pdfX = (pw - pdfW) / 2
  const pdfY = (ph - pdfH) / 2

  // Dev-mode guard: catch any future regression that reintroduces
  // independent width/height calculations (the original bug that caused
  // horizontally condensed text in PDFs vs PNGs).
  if (import.meta.env?.DEV) {
    const canvasRatio = canvas.width / canvas.height
    const pdfRatio = pdfW / pdfH
    if (Math.abs(canvasRatio - pdfRatio) > 0.01) {
      console.warn('[exportElementPDF] aspect ratio mismatch — image will be distorted', {
        canvas: `${canvas.width}x${canvas.height} (ratio ${canvasRatio.toFixed(4)})`,
        pdf: `${pdfW.toFixed(1)}x${pdfH.toFixed(1)}mm (ratio ${pdfRatio.toFixed(4)})`,
        filename,
      })
    }
  }

  doc.addImage(canvas.toDataURL('image/png', 1.0), 'PNG', pdfX, pdfY, pdfW, pdfH, '', 'FAST')
  doc.save(filename)
  return filename
}

/**
 * Pad a canvas to a target aspect ratio, centering the original content on a
 * white (or specified) background. Used to make PNG exports match the full
 * page size of their PDF counterparts (e.g. full A4) instead of being cropped
 * to the content height.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {number} targetW - Target width in px (at the canvas's scale)
 * @param {number} targetH - Target height in px (at the canvas's scale)
 * @param {string} [bg='#ffffff']
 * @returns {HTMLCanvasElement} new padded canvas
 */
function padCanvasToSize(canvas, targetW, targetH, bg = '#ffffff') {
  // If the canvas is already the right size or larger, return as-is.
  if (canvas.width >= targetW && canvas.height >= targetH) return canvas

  const padded = document.createElement('canvas')
  padded.width = Math.max(targetW, canvas.width)
  padded.height = Math.max(targetH, canvas.height)
  const ctx = padded.getContext('2d')
  ctx.fillStyle = bg
  ctx.fillRect(0, 0, padded.width, padded.height)
  // Center horizontally, pin to top (content flows from top of page).
  const x = Math.round((padded.width - canvas.width) / 2)
  ctx.drawImage(canvas, x, 0)
  return padded
}

// A4 dimensions in px at 96dpi (used for PNG padding to match PDF page size)
const A4_PORTRAIT_PX = { w: 794, h: 1123 }
const A4_LANDSCAPE_PX = { w: 1123, h: 794 }

/**
 * Export a DOM element to a PNG file.
 * @param {HTMLElement} element
 * @param {string} filename
 * @param {Object} [options]
 * @param {number} [options.scale=3]
 * @param {string} [options.backgroundColor='#ffffff']
 * @param {boolean|string} [options.padToPageSize] - 'a4-portrait' | 'a4-landscape' | false
 *        When set, the PNG is padded to the full A4 page size so it matches
 *        the PDF output (which always fills a full page).
 * @returns {Promise<string>} filename
 */
export async function exportElementPNG(element, filename, options = {}) {
  const scale = options.scale || 3
  let canvas = await captureElementToCanvas(element, {
    scale,
    backgroundColor: options.backgroundColor,
  })

  // Pad to full page size so the PNG matches the PDF (which always fills a
  // full A4 page) instead of being cropped to the content height.
  if (options.padToPageSize === 'a4-portrait') {
    canvas = padCanvasToSize(canvas, A4_PORTRAIT_PX.w * scale, A4_PORTRAIT_PX.h * scale, options.backgroundColor)
  } else if (options.padToPageSize === 'a4-landscape') {
    canvas = padCanvasToSize(canvas, A4_LANDSCAPE_PX.w * scale, A4_LANDSCAPE_PX.h * scale, options.backgroundColor)
  }

  saveCanvasAsPNG(canvas, filename)
  return filename
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Receipt exports                                                           */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Resolve a receipt object from either an object or a payment id, used only to
 * derive a sensible filename. The actual visual output always comes from the
 * captured DOM element.
 */
async function resolveReceiptForFilename(receiptOrPaymentId) {
  if (receiptOrPaymentId && typeof receiptOrPaymentId === 'object') return receiptOrPaymentId
  try {
    const { default: api } = await import('../services/api')
    const { data } = await api.get(`/finance/receipts/${receiptOrPaymentId}`)
    return data.receipt
  } catch {
    return {}
  }
}

/**
 * Export the on-screen receipt (.receipt-document) as a landscape A4 PDF.
 * @param {Object|number|string} receiptOrPaymentId - Receipt object (for filename) or payment id
 * @param {Object} [options]
 * @param {HTMLElement} [options.element] - The .receipt-document node to capture
 */
export async function exportReceiptPDF(receiptOrPaymentId, options = {}) {
  const target = options.element || document.querySelector('.receipt-document')
  if (!target) throw new Error('Receipt element not found on page')

  const receipt = await resolveReceiptForFilename(receiptOrPaymentId)
  const filename = `Receipt-${receipt?.receipt_number || receipt?.payment_id || Date.now()}.pdf`
  return exportElementPDF(target, filename, { orientation: 'landscape', format: 'a4' })
}

/**
 * Export the on-screen receipt (.receipt-document) as a PNG image.
 * @param {Object|number|string} receiptOrPaymentId
 * @param {Object} [options]
 * @param {HTMLElement} [options.element]
 */
export async function exportReceiptPNG(receiptOrPaymentId, options = {}) {
  const target = options.element || document.querySelector('.receipt-document')
  if (!target) throw new Error('Receipt element not found on page')

  const receipt = await resolveReceiptForFilename(receiptOrPaymentId)
  const filename = `Receipt-${receipt?.receipt_number || receipt?.payment_id || Date.now()}.png`
  return exportElementPNG(target, filename, { padToPageSize: 'a4-landscape' })
}

/**
 * Legacy alias — exportPaymentPDF now routes through the same shared path.
 * @param {Object} payment
 */
export async function exportPaymentPDF(payment) {
  return exportReceiptPDF(payment)
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Invoice exports                                                           */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Export the on-screen invoice (.invoice-document) as a portrait A4 PDF.
 * @param {Object} invoice - Invoice object (used for filename)
 * @param {Object} [options]
 * @param {HTMLElement} [options.element] - The .invoice-document node to capture
 */
export async function exportInvoicePDF(invoice, options = {}) {
  const target = options.element || document.querySelector('.invoice-document')
  if (!target) throw new Error('Invoice element not found on page')

  const filename = `Invoice-${invoice?.invoice_number || invoice?.id || Date.now()}.pdf`
  return exportElementPDF(target, filename, { orientation: 'landscape', format: 'a4' })
}

/**
 * Export the on-screen invoice (.invoice-document) as a PNG image.
 * @param {Object} invoice
 * @param {Object} [options]
 * @param {HTMLElement} [options.element]
 */
export async function exportInvoicePNG(invoice, options = {}) {
  const target = options.element || document.querySelector('.invoice-document')
  if (!target) throw new Error('Invoice element not found on page')

  const filename = `Invoice-${invoice?.invoice_number || invoice?.id || Date.now()}.png`
  // Pad to full A4 landscape so the PNG matches the PDF page size exactly.
  return exportElementPNG(target, filename, { padToPageSize: 'a4-landscape' })
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Generic report exports (DOM-based, share the same capture path)           */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Export any on-screen report element as a PDF.
 * @param {HTMLElement} element - The report DOM node to capture
 * @param {string} title - Used to build the filename
 * @param {Object} [options]
 */
export async function exportReportPDF(element, title, options = {}) {
  if (!element) throw new Error('Report element not provided')
  const slug = (title || 'report').toLowerCase().replace(/\s+/g, '-')
  const filename = `${slug}-${Date.now()}.pdf`
  return exportElementPDF(element, filename, {
    orientation: options.orientation || 'landscape',
    format: options.format || 'a4',
  })
}

/**
 * Export any on-screen report element as a PNG image.
 * @param {HTMLElement} element
 * @param {string} title
 */
export async function exportReportPNG(element, title) {
  if (!element) throw new Error('Report element not provided')
  const slug = (title || 'report').toLowerCase().replace(/\s+/g, '-')
  const filename = `${slug}-${Date.now()}.png`
  return exportElementPNG(element, filename)
}
